<template>
  <div>
    <div class="d-flex align-items-center">
      <h1 class="h3">
        {{ $t('returnReceived.title') }}
      </h1>
      <div class="ml-auto">
        <a-button
          v-if="params.warehouse_id && permission.includes('WRITE') && !flagWms"
          type="primary"
          size="large"
          @click="navigateToCreate"
        >
          <a-icon type="plus-square" />
          {{ $t('returnReceived.createReturn') }}
        </a-button>
      </div>
    </div>
    <a-row :gutter="24" class="mt-4">
      <a-col :span="9">
        <a-select
          v-model="params.warehouse_id"
          :placeholder="$t('fulfillment.allWarehouses')"
          class="select-antd-default"
          size="large"
          show-search
          option-filter-prop="children"
          :filter-option="filterOption"
          :allow-clear="warehouseList.length > 1"
          :disabled="loading || isLoading"
          @change="handleChangeWarehouse"
        >
          <a-select-option
            v-for="item in warehouseList"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
        <a-alert
          v-if="!params.warehouse_id"
          class="mt-2"
          style="font-size: 13px"
          :message="$t('productAvailability.warningReturn')"
          type="warning"
          show-icon
        />
      </a-col>
      <a-col :span="10">
        <a-input-search
          v-model="params.query"
          size="large"
          :placeholder="$t('returnReceived.searchPlaceholder')"
          :loading="loading || isLoading"
          :disabled="loading || isLoading"
          allow-clear
          @search="handleSearch"
        >
          <a-button slot="enterButton" type="danger" icon="search">
            {{ $t('utils.search') }}
          </a-button>
        </a-input-search>
      </a-col>
    </a-row>
    <div class="mt-4">
      <a-table
        class="gr-list"
        :data-source="returns"
        :row-key="record => record.id"
        :columns="columns"
        :loading="loading || isLoading"
        :pagination="false"
        :scroll="scroll"
      >
        <template slot="number" slot-scope="text">
          <a-tooltip placement="topRight">
            <template #title>
              <span>{{ text }}</span>
            </template>
            {{ text }}
          </a-tooltip>
        </template>
        <template slot="date" slot-scope="text">
          {{ text ? $moment(text).format('HH:mm:ss, DD MMM YYYY') : '-' }}
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="colors[text]" style="width: 80px; text-align: center">
            {{ $t(`returnReceived.status_${text.toLowerCase()}`) }}
          </a-tag>
        </template>
        <template slot="action" slot-scope="text, record">
          <a-dropdown placement="bottomRight">
            <a @click.prevent="">
              <a-icon type="more" />
            </a>
            <template #overlay>
              <a-menu>
                <a-menu-item
                  key="1"
                  class="py-2"
                  @click="navigateToDetail(record)"
                >
                  {{ $t('returnReceived.detailReturn') }}
                </a-menu-item>
                <a-menu-item
                  v-if="record?.status?.toLowerCase() === 'return'"
                  key="2"
                  class="py-2"
                  @click="confirmCancelReturnReceived(record)"
                >
                  {{ $t('returnReceived.cancelReturn') }}
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </template>
      </a-table>
    </div>
    <div class="mt-4 text-right">
      <a-pagination
        v-model="params.page"
        :page-size="params.size"
        :page-size-options="sizeOptions"
        :total="total_row"
        :show-total="(total, range) => $t('utils.pagination_show_total', { perpage: `${range[0]} - ${range[1]}`, total })"
        :locale="{ items_per_page: $t('utils.items_per_page') }"
        :disabled="loading || isLoading"
        show-size-changer
        @change="handlePageChange"
        @showSizeChange="handleShowSizeChange"
      />
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { getReturnReceivedList, cancelReturnReceived } from '@/api/inventory'
import { getWarehouseInternal } from '@/api/warehouse'

export default {
  data: function () {
    return {
      screenHeight: ((screen.height - 900) * 0.5) + (screen.height * 0.5),
      total_row: 0,
      loading: false,
      isLoading: false,
      flagWms: false,
      businessList: [],
      warehouseList: [],
      returns: [],
      sizeOptions: ['10', '20', '30', '40', '50'],
      colors: {
        RETURN: 'blue',
        RETURN_RELEASE: 'red',
      },
      params: {
        warehouse_id: undefined,
        query: undefined,
        page: 1,
        size: 10,
      },
    }
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t('returnReceived.returnNumber'),
          key: 'number',
          dataIndex: 'number',
          width: 250,
          ellipsis: true,
          scopedSlots: { customRender: 'number' },
        },
        {
          title: this.$t('returnReceived.returnDate'),
          key: 'date',
          dataIndex: 'date',
          ellipsis: true,
          scopedSlots: { customRender: 'date' },
        },
        {
          title: this.$t('fulfillment.orderNo'),
          key: 'order_number',
          dataIndex: 'order_number',
          ellipsis: true,
          scopedSlots: { customRender: 'order_number' },
        },
        {
          title: this.$t('fulfillment.status'),
          key: 'status',
          width: 150,
          dataIndex: 'status',
          ellipsis: true,
          scopedSlots: { customRender: 'status' },
        },
        {
          title: '',
          key: 'id',
          dataIndex: 'id',
          scopedSlots: { customRender: 'action' },
          width: 50,
        },
      ]
    },
    businessId() {
      return (this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id) || ''
    },
    scroll() {
      return {
        y: this.screenHeight > 450
          ? this.screenHeight
          : 360,
        x: 1000,
      }
    },
    tHeight() {
      return !this.products.length ? '0px'
        : this.screenHeight > 450 ? `${this.screenHeight}px`
        : '450px'
    },
    permission() {
      return this.$store.getters['user/can']('goods-receipt')
    },
  },
  watch: {
    businessId() {
      this.init()
    },
  },
  created() {
    this.init()
  },
  mounted() {
    this.init()
  },
  methods: {
    init: debounce(async function() {
      this.loading = true
      await Promise.all([
        this.getBusinessList(),
        this.getWarehouseList(),
        this.fetchReturnReceivedList(),
      ])
        .then(response => {
          this.businessList = response[0]
          this.warehouseList = response[1]
          this.returns = response[2]
          const filter = this.warehouseList.filter(v => {return v.value === this.params.warehouse_id})
          this.flagWms = filter?.[0]?.wms_code === null || filter?.[0]?.wms_code?.toUpperCase() === 'N/A' || filter?.[0]?.wms_code === '' ? false : true
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => this.loading = false)
    }, 500),
    async getBusinessList() {
      return await this.$store
        .dispatch('business/GETBUSINESS', {
          business_id: this.businessId,
        })
        .then(({ data }) => data)
        .catch(err => {
          console.error(err)
          return []
        })
    },
    async getWarehouseList() {
      return await getWarehouseInternal({
        business_id: this.businessId,
        limit: 500,
      })
        .then(res => {
          const raw = res?.data?.data || res?.data || []
          const response = raw.map(w => ({
            ...w,
            value: String(w.id || w.warehouse_id),
            label: String(w.name || w.warehouse_name || '').trim(),
          }))

          this.params.warehouse_id = this.$store.state.user.restriction_base === 'Warehouse'
            ? this.$route.query.warehouse_id
            : this.$store.state.user.restriction_base !== 'Warehouse' && response.length === 1
            ? response[0].value
            : undefined

          return response
        })
        .catch(err => {
          this.params.warehouse_id = undefined
          console.error(err)
          return []
        })
    },
    async fetchReturnReceivedList() {
      this.isLoading = true
      return await getReturnReceivedList({
        business_id: this.businessId,
        params: {
          business_id: this.businessId,
          warehouse_id: this.params.warehouse_id,
          query: this.params.query,
        },
      })
      .then(({ data: response }) => {
        this.total_row = response?.total_items || 0
        return response?.data || []
      })
      .catch(err => {
        this.total_row = 0
        console.error(err)
        return []
      })
      .finally(() => this.isLoading = false)
    },
    async handleChangeWarehouse(value) {
      if(value) {
        const filter = this.warehouseList.filter(warehouse => { return warehouse.value === value })
        this.flagWms = filter?.[0]?.wms_code === null
          || filter?.[0]?.wms_code?.toUpperCase() === 'N/A'
          || filter?.[0]?.wms_code === ''
            ? false
            : true
      }
      this.params.page = 1
      this.returns = await this.fetchReturnReceivedList()
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text.toLowerCase().includes(input.toLowerCase())
      )
    },
    async handleSearch() {
      this.params.page = 1
      this.returns = await this.fetchReturnReceivedList()
    },
    async handlePageChange(pageNumber) {
      this.params.page = pageNumber
      this.returns = await this.fetchReturnReceivedList()
    },
    async handleShowSizeChange(current, pageSize) {
      this.params.page = current
      this.params.size = pageSize
      this.returns = await this.fetchReturnReceivedList()
    },
    async handleCancelReturnReceived(data) {
      this.isLoading = true
      await cancelReturnReceived({
        business_id: this.businessId,
        data,
      })
      .then(async () => {
        this.$notification.success({
          message: this.$t('returnReceived.cancelSuccess'),
        })
        this.returns = await this.fetchReturnReceivedList()
      })
      .catch(err => {
        this.$notification.error({
          message: this.$t('returnReceived.cancelFailed'),
        })
        console.error(err)
      })
      .finally(() => this.isLoading = false)
    },
    confirmCancelReturnReceived(record) {
      this.$confirm({
        icon: () => null,
        title: this.$t('returnReceived.cancelConfirmTitle'),
        content: this.$t('returnReceived.cancelConfirmContent', { number: record.number }),
        okText: this.$t('fulfillment.cancel'),
        onOk: () => this.handleCancelReturnReceived(record),
      })
    },
    navigateToCreate() {
      this.$router.push({
        name: 'inventory.return.received.create',
        query: {
          ...this.$route.query,
          warehouseId: this.params.warehouse_id,
        },
      })
    },
    navigateToDetail(record) {
      const { id, number } = record
      this.$router.push({
        name: 'inventory.return.received.create',
        query: {
          ...this.$route.query,
          id,
          number_return: number,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.select-antd-default.ant-select-lg {
  width: 100%;

  .ant-select-selection__rendered {
    line-height: 38px !important;
  }
}
</style>